import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { CartCheckoutClient } from '../cartCheckoutClient';
import { reducer } from './reducer';
import { ContextValue } from './types';

export const CartAndDeliveryDatesDataContext =
  createContext<ContextValue>(undefined);

export const CartAndDeliveryDatesDataProvider: React.FC<{
  client: CartCheckoutClient;
  children: React.ReactNode;
}> = ({ client, children }) => {
  const [state, dispatch] = useReducer(reducer, {
    cart: null,
    cartLoading: false,
    deliveryDates: null,
    deliveryDatesLoading: false,
    errors: [],
  });

  const [hasStartedInitialLoad, setHasStartedInitialLoad] = useState(false);

  useEffect(() => {
    const { cartLoading, deliveryDatesLoading } = state;
    if (!hasStartedInitialLoad && (cartLoading || deliveryDatesLoading)) {
      setHasStartedInitialLoad(true);
    }
  }, [state, hasStartedInitialLoad]);

  const value = useMemo(() => {
    const { cart, cartLoading, deliveryDates, deliveryDatesLoading, errors } =
      state;

    return {
      cart,
      cartLoading,
      deliveryDates,
      deliveryDatesLoading,
      errors,
      hasStartedInitialLoad,
      dispatch,
      client,
    };
  }, [client, state, hasStartedInitialLoad]);

  return (
    <CartAndDeliveryDatesDataContext.Provider value={value}>
      {children}
    </CartAndDeliveryDatesDataContext.Provider>
  );
};

export const CheckoutDataProvider = ({
  children,
  metricName,
}: {
  children: React.ReactNode;
  // NOTE(JJ): metricName is used to identify the page which us using this client
  // e.g, cart, check-out
  metricName: string;
}) => {
  const [client] = useState(
    () =>
      new CartCheckoutClient({
        logging: {
          context: 'CartAndDeliveryDatesDataContext/apiClient',
          metricName,
        },
      })
  );

  return (
    <CartAndDeliveryDatesDataProvider client={client}>
      {children}
    </CartAndDeliveryDatesDataProvider>
  );
};

export function useCartAndDeliveryDatesDataContext() {
  const context = useContext<ContextValue>(CartAndDeliveryDatesDataContext);

  if (context === undefined) {
    throw new Error(
      `useCartAndDeliveryDatesDataContext must be used within a CartAndDeliveryDatesData Provider`
    );
  }

  return context;
}
